import React from 'react';
import {
  Box,
  Card,
  CardContent,
  FormControlLabel,
  Slider,
  Switch,
  Typography,
} from '@mui/material';

import {
  MAX_EDGE_PADDING_PX,
  MIN_EDGE_PADDING_PX,
  useLayoutPreferences,
} from '../contexts/LayoutPreferencesContext';
import { useThemeMode } from '../contexts/ThemeContext';
import { useLayoutTokens } from '../hooks/useLayoutTokens';

/**
 * Profile → Theme: dark mode plus the screen-edge padding floor.
 *
 * Edge padding is the minimum pixel gutter between page content (titles,
 * subtitles, everything inside PageShell) and the screen edge. It is a floor
 * over the Display & Layout side-padding preset, so Edge-to-edge keeps its
 * uncapped width while titles keep a few pixels of breathing room. Both
 * settings are device-scoped, matching the other appearance preferences.
 */
const EDGE_PADDING_MARKS = [0, 8, 16, 24, 32, 40, 48].map((value) => ({ value }));

const ThemeSettingsSection: React.FC = () => {
  const { darkMode, toggleDarkMode } = useThemeMode();
  const { edgePaddingPx, setEdgePaddingPx } = useLayoutPreferences();
  const { cardContentPaddingSx } = useLayoutTokens();

  return (
    <Card>
      <CardContent sx={cardContentPaddingSx}>
        <Typography variant="h6" gutterBottom>
          Theme
        </Typography>
        <FormControlLabel
          control={
            <Switch checked={darkMode} onChange={toggleDarkMode} color="primary" />
          }
          label="Dark Mode"
        />

        <Box sx={{ mt: 2 }}>
          <Typography variant="subtitle1" sx={{ fontWeight: 600, mb: 1 }}>
            Screen edge padding
          </Typography>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
            Minimum space between page content and the edge of the screen, in
            pixels. Applies on top of the side-padding preset, so titles keep
            some breathing room even in Edge-to-edge. Saved on this device.
          </Typography>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, maxWidth: 360 }}>
            <Slider
              value={edgePaddingPx}
              min={MIN_EDGE_PADDING_PX}
              max={MAX_EDGE_PADDING_PX}
              step={1}
              marks={EDGE_PADDING_MARKS}
              valueLabelDisplay="auto"
              aria-label="Screen edge padding"
              onChange={(_event, value) => {
                if (typeof value === 'number') setEdgePaddingPx(value);
              }}
            />
            <Typography variant="body2" color="text.secondary" sx={{ whiteSpace: 'nowrap' }}>
              {edgePaddingPx} px
            </Typography>
          </Box>
        </Box>
      </CardContent>
    </Card>
  );
};

export default ThemeSettingsSection;
